import { useMemo, type ReactNode } from "react";

import type { PMIssue } from "@/domains/pm/types";
import { type useStagedSchedule } from "@/domains/pm/hooks/useStagedSchedule";
import { ScheduleChangeContext, type ScheduleChangeApi } from "./scheduleChangeContext";
import { ScheduleStagingContext, type ScheduleStagingApi } from "./scheduleStagingContext";

/**
 * Holds the Gantt's schedule edits back until Confirm.
 *
 * It works by OVERRIDING `ScheduleChangeContext` rather than by threading a
 * flag into every control. Each date control on the page already asks that
 * context to make the change — the bar drag, the Unscheduled band's pickers,
 * the right-click date submenu, the detail panel — so all of them stage while
 * this is mounted, and none of them knows it.
 *
 * Mounted only while the Gantt is the active view. Rearranging a schedule is
 * exploratory; editing a list is not.
 *
 * The buffer and the Confirm round trip both belong to the PAGE — the view
 * tabs and the unsaved-changes bar sit above this in the tree — so this
 * publishes the two contexts and owns nothing.
 */
export function ScheduleStagingProvider({
  buffer,
  staging,
  children,
}: {
  buffer: ReturnType<typeof useStagedSchedule>;
  staging: ScheduleStagingApi;
  children: ReactNode;
}) {
  const { stageDates } = buffer;

  // Every date control on the page reaches this instead of the repository.
  const scheduleApi = useMemo<ScheduleChangeApi>(
    () => ({
      request: async (issue: PMIssue, patch) => {
        const start = "start_date" in patch ? (patch.start_date ?? null) : issue.start_date;
        const due = "due_date" in patch ? (patch.due_date ?? null) : issue.due_date;
        stageDates(issue.id, start, due);
      },
      // Nothing is written yet, so nothing can cascade yet. The whole point of
      // staging is that the cascade is computed once, at Confirm.
      hasDependents: () => false,
      applyWithCascade: async (issue: PMIssue, start, due) => {
        stageDates(issue.id, start, due);
        return null;
      },
    }),
    [stageDates]
  );

  return (
    <ScheduleStagingContext.Provider value={staging}>
      <ScheduleChangeContext.Provider value={scheduleApi}>
        {children}
      </ScheduleChangeContext.Provider>
    </ScheduleStagingContext.Provider>
  );
}
